// @ts-nocheck
/**
 * TocPanel — React-based scrollable Table of Contents flyout.
 *
 * Replaces the DOM-based hamburger TOC for /web/ static pages.
 * Receives pre-grouped chapter data (same structure createHamburgerMenu uses)
 * and renders an expand/collapse tree. Uses scrollToElement from hamburgerMenu.js
 * for smooth-scroll + hash-update behaviour identical to the DOM version.
 *
 * Props:
 *   chapterGroups — Array<{ heading: { id, text }, topics: Array<{ id, text }> }>
 *   onNavigate    — called after a link is clicked (e.g. to close the flyout)
 *   maxHeight     — CSS string for the scrollable area max-height (default "60vh")
 */
import React, { useState, useCallback } from "react";
import { scrollToElement } from "../utils/hamburgerMenu";

const font = "'Open Sans', system-ui, sans-serif";

// ── Icons ──────────────────────────────────────────────────────────────────

const ChevronRight = ({ expanded }) => (
  <svg
    width="14" height="14" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2"
    style={{
      display: "block", flexShrink: 0,
      transition: "transform 0.18s ease",
      transform: expanded ? "rotate(90deg)" : "none",
    }}
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const ArrowRight = ({ size = 12 }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2"
    style={{ display: "block", flexShrink: 0, transition: "transform 0.15s ease" }}
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

// ── TocPanel ───────────────────────────────────────────────────────────────

export default function TocPanel({ chapterGroups = [], onNavigate, maxHeight = "60vh" }) {
  // Track which chapters are expanded by index
  const [expanded, setExpanded] = useState({});

  const toggleChapter = useCallback((i) => {
    setExpanded(prev => ({ ...prev, [i]: !prev[i] }));
  }, []);

  const handleNav = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) scrollToElement(el, id);
    if (onNavigate) onNavigate();
  }, [onNavigate]);

  if (!chapterGroups.length) return null;

  return (
    <div style={panelStyle}>
      {/* Scrollable TOC list */}
      <ul style={{ ...listStyle, maxHeight }} role="tree">
        {chapterGroups.map((group, i) => {
          const isExpanded = !!expanded[i];
          return (
            <li key={group.heading.id || i} role="treeitem" aria-expanded={isExpanded} style={chapterItemStyle}>

              {/* Chapter row: [chevron] [title] [→ arrow] */}
              <div style={chapterRowStyle}>
                {/* Expand/collapse toggle */}
                <button
                  type="button"
                  onClick={() => toggleChapter(i)}
                  aria-label={isExpanded ? "Collapse" : "Expand"}
                  style={expandBtnStyle}
                  onMouseDown={e => e.preventDefault()} // prevent focus ring on mouse click
                >
                  <ChevronRight expanded={isExpanded} />
                </button>

                {/* Chapter title — click to toggle expand */}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleChapter(i)}
                  onKeyDown={e => (e.key === "Enter" || e.key === " ") && toggleChapter(i)}
                  style={chapterLabelStyle}
                >
                  {group.heading.text}
                </span>

                {/* Navigate arrow */}
                {group.heading.id && (
                  <button
                    type="button"
                    onClick={() => handleNav(group.heading.id)}
                    aria-label={`Go to ${group.heading.text}`}
                    style={navArrowBtnStyle}
                    onMouseDown={e => e.preventDefault()}
                    className="toc-nav-arrow-btn"
                  >
                    <ArrowRight size={13} />
                  </button>
                )}
              </div>

              {/* Topics list */}
              {isExpanded && group.topics.length > 0 && (
                <ul style={topicsListStyle} role="group">
                  {group.topics.map(topic => (
                    <li key={topic.id} style={topicItemStyle}>
                      <div style={topicRowStyle}>
                        <button
                          type="button"
                          onClick={() => handleNav(topic.id)}
                          style={topicLinkStyle}
                          onMouseDown={e => e.preventDefault()}
                        >
                          {topic.text}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleNav(topic.id)}
                          aria-label={`Go to ${topic.text}`}
                          style={{ ...navArrowBtnStyle, padding: "4px" }}
                          onMouseDown={e => e.preventDefault()}
                          className="toc-nav-arrow-btn"
                        >
                          <ArrowRight size={11} />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────

const panelStyle = {
  background: "#fff",
  borderRadius: "0 0 8px 8px",
  fontFamily: font,
  overflowY: "auto",
  scrollbarWidth: "thin",
  scrollbarColor: "rgba(0,102,204,0.25) transparent",
};

const listStyle = {
  listStyle: "none",
  margin: 0,
  padding: 0,
  overflowY: "auto",
  scrollbarWidth: "thin",
  scrollbarColor: "rgba(0,102,204,0.25) transparent",
};

const chapterItemStyle = {
  borderBottom: "1px solid rgba(240,240,240,0.9)",
};

const chapterRowStyle = {
  display: "flex",
  alignItems: "center",
  padding: "10px 12px 10px 8px",
  background: "#fafafa",
  gap: "4px",
};

const expandBtnStyle = {
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "4px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#666",
  borderRadius: "3px",
  flexShrink: 0,
};

const chapterLabelStyle = {
  flex: 1,
  fontSize: "0.88rem",
  fontWeight: 600,
  color: "#222c47",
  cursor: "pointer",
  wordBreak: "break-word",
  lineHeight: 1.35,
  padding: "2px 0",
  userSelect: "none",
};

const navArrowBtnStyle = {
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "6px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#0066cc",
  borderRadius: "4px",
  flexShrink: 0,
  marginLeft: "auto",
};

const topicsListStyle = {
  listStyle: "none",
  margin: 0,
  padding: 0,
  background: "#fff",
};

const topicItemStyle = {
  borderBottom: "1px solid rgba(248,249,250,0.9)",
};

const topicRowStyle = {
  display: "flex",
  alignItems: "center",
  padding: "8px 12px 8px 36px",
  gap: "4px",
};

const topicLinkStyle = {
  flex: 1,
  background: "none",
  border: "none",
  cursor: "pointer",
  textAlign: "left",
  fontSize: "0.78rem",
  fontWeight: 400,
  color: "#444",
  padding: "2px 0",
  wordBreak: "break-word",
  lineHeight: 1.35,
  fontFamily: font,
};
